import { useMemo, useState } from 'react';
import type { Device, Preset } from '@glyph/protocol-ts';
import { fmtAgo, isLocalShell, useClient, useDevices, useMe, usePresets } from '@glyph/core';
import {
  Button,
  Card,
  CardHeader,
  CellMono,
  Checkbox,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  IconButton,
  ListRow,
  Note,
  PageHeader,
  Section,
  Select,
  Switch,
  Tag,
  TextArea,
  TextInput,
  useToast,
} from '@glyph/ui';

/** Sections a cloud preset can carry, in the order people think about them (the cloud enforces the same list). */
export const PRESET_SECTIONS: { key: string; label: string; help: string }[] = [
  { key: 'display', label: 'Captions look', help: 'font, colour, position, box, caption mode and timing' },
  { key: 'deepgram', label: 'Deepgram', help: 'model, language, endpointing, formatting' },
  { key: 'audio', label: 'Audio', help: 'input, channel, gain, silence alert' },
  { key: 'outputs', label: 'Outputs', help: 'HDMI policy and video mode' },
  { key: 'record', label: 'Recording', help: 'what gets recorded and kept' },
  { key: 'wordlib', label: 'Word library', help: 'key terms and replacements' },
  { key: 'translation', label: 'Translation', help: 'translated caption track' },
  { key: 'network', label: 'Network', help: 'hotspot band and channel, Wi-Fi band, LTE, DNS (never a unit address or hotspot password)' },
];
const LABEL = Object.fromEntries(PRESET_SECTIONS.map((s) => [s.key, s.label]));
const MANAGE = new Set(['platform', 'owner', 'admin', 'event_admin']);
const APPLY = new Set(['platform', 'owner', 'admin', 'event_admin', 'operator']);

const sectionsTxt = (p: Preset) => p.sections.map((s) => LABEL[s] ?? s).join(' · ') || 'Nothing yet';
const unitsOf = (list: Device[] | undefined) => (list ?? []).filter((d) => d.status !== 'pending');

/** Pick units: every unit the account can see, online state alongside. */
function UnitPicker({ units, value, onChange }: { units: Device[]; value: string[]; onChange: (v: string[]) => void }) {
  const all = units.length > 0 && units.every((d) => value.includes(d.id));
  return (
    <div className="flex flex-col gap-1">
      <label className="flex items-center gap-2 py-1 text-base font-medium">
        <Checkbox checked={all} onChange={() => onChange(all ? [] : units.map((d) => d.id))} label="All units" />
        <span>All units</span>
      </label>
      {units.map((d) => (
        <label key={d.id} className="flex items-center gap-2 py-1 text-base">
          <Checkbox checked={value.includes(d.id)} onChange={() => onChange(value.includes(d.id) ? value.filter((x) => x !== d.id) : [...value, d.id])} label={`Apply to ${d.name}`} />
          <span className="min-w-0 flex-1 truncate">{d.name}</span>
          <span className="font-mono text-xs text-fg4">{d.online ? 'online' : 'gets it on reconnect'}</span>
        </label>
      ))}
    </div>
  );
}

/** Apply a cloud preset to units. Used by the Presets page and the Devices page's group actions. */
export function ApplyPresetDialog({ open, onClose, preset, targets }: { open: boolean; onClose: () => void; preset?: Preset | null; targets?: string[] }) {
  const presets = usePresets(open);
  const devices = useDevices();
  const toast = useToast();
  const units = unitsOf(devices.data);
  const [presetId, setPresetId] = useState('');
  const [sel, setSel] = useState<string[] | null>(null);
  const [keep, setKeep] = useState(true);
  if (!open) return null;
  const chosen = preset ?? presets.data?.find((p) => p.id === presetId) ?? null;
  const picked = sel ?? targets ?? [];
  const submit = async () => {
    if (!chosen || !picked.length) return;
    try {
      const r = await presets.apply({ id: chosen.id, device_ids: picked, save_on_unit: keep });
      const n = (s: string) => r.results.filter((x) => x.status === s).length;
      const bad = r.results.filter((x) => x.status === 'error' || x.status === 'forbidden');
      const name = (id: string) => units.find((d) => d.id === id)?.name ?? id;
      toast.push(
        `${chosen.name}: ${n('sent')} applied${n('queued') ? `, ${n('queued')} queued until online` : ''}${n('partial') ? `, ${n('partial')} without Wi-Fi` : ''}${
          bad.length ? `, refused on ${bad.map((b) => name(b.device_id)).join(', ')}` : ''
        }`,
        bad.length ? 'warn' : 'ok',
      );
      setSel(null);
      onClose();
    } catch (e) {
      toast.push((e as Error).message || 'Could not apply the preset', 'bad');
    }
  };
  return (
    <Dialog
      open
      onClose={() => {
        setSel(null);
        onClose();
      }}
      title={chosen ? `Apply ${chosen.name}` : 'Apply a preset'}
      meta={`${picked.length} UNIT${picked.length === 1 ? '' : 'S'} · OFFLINE UNITS GET IT ON RECONNECT`}
      confirm={{ label: 'Apply preset', onClick: submit, disabled: !chosen || !picked.length || presets.busy, busy: presets.busy }}
    >
      <div className="flex flex-col gap-3">
        {!preset && (
          <Field label="Preset">
            <Select
              block
              value={presetId}
              onChange={setPresetId}
              options={[{ value: '', label: (presets.data ?? []).length ? 'Pick a preset…' : 'No presets yet' }, ...(presets.data ?? []).map((p) => ({ value: p.id, label: p.name }))]}
              aria-label="Preset"
            />
          </Field>
        )}
        {chosen && <Note>Sets {sectionsTxt(chosen)}{chosen.wifi.length ? ` · adds Wi-Fi ${chosen.wifi.map((w) => w.ssid).join(', ')} (online units only)` : ''}.</Note>}
        {targets ? (
          <Note>{units.filter((d) => picked.includes(d.id)).map((d) => d.name).join(', ') || 'No units ticked'}</Note>
        ) : (
          <UnitPicker units={units} value={picked} onChange={setSel} />
        )}
        <label className="flex items-center gap-2 text-base">
          <Checkbox checked={keep} onChange={setKeep} label="Keep it in each unit's own preset list" />
          <span>Keep it in each unit's own preset list</span>
        </label>
      </div>
    </Dialog>
  );
}

type Draft = { id?: string; name: string; description: string; settings: Record<string, string>; wifi: { ssid: string; password: string; hidden: boolean; has_password: boolean }[] };
const toDraft = (p?: Preset): Draft => ({
  id: p?.id,
  name: p?.name ?? '',
  description: p?.description ?? '',
  settings: Object.fromEntries(Object.entries(p?.settings ?? {}).map(([k, v]) => [k, JSON.stringify(v, null, 2)])),
  wifi: (p?.wifi ?? []).map((w) => ({ ssid: w.ssid, password: '', hidden: w.hidden, has_password: w.has_password })),
});

/** Presets: the cloud library every unit can use. Save one from a unit, copy a unit's own presets in, edit any
 * section, add Wi-Fi networks, and apply to any units. */
export function PresetsScreen() {
  const me = useMe();
  const presets = usePresets();
  const devices = useDevices();
  const toast = useToast();
  const role = me.data?.role ?? 'viewer';
  const canManage = MANAGE.has(role);
  const canApply = APPLY.has(role);
  const units = unitsOf(devices.data);
  const online = units.filter((d) => d.online);
  const list = presets.data ?? [];
  const [applying, setApplying] = useState<Preset | null>(null);
  const [capture, setCapture] = useState<null | { device_id: string; name: string; description: string; sections: string[] }>(null);
  const [importing, setImporting] = useState<null | { device_id: string; overwrite: boolean }>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [removing, setRemoving] = useState<Preset | null>(null);
  const unitOptions = useMemo(() => [{ value: '', label: online.length ? 'Pick a unit…' : 'No unit online' }, ...online.map((d) => ({ value: d.id, label: d.name }))], [online]);
  const err = (e: unknown) => toast.push((e as Error).message || 'Something went wrong', 'warn');

  const doCapture = async () => {
    if (!capture) return;
    try {
      const p = await presets.capture({ ...capture, name: capture.name.trim() });
      toast.push(`Saved ${p.name} from ${units.find((d) => d.id === capture.device_id)?.name ?? 'the unit'}`, 'ok');
      setCapture(null);
    } catch (e) {
      err(e);
    }
  };
  const doImport = async () => {
    if (!importing) return;
    try {
      const r = await presets.importFrom(importing);
      toast.push(
        r.imported.length ? `Copied ${r.imported.join(', ')}${r.skipped.length ? ` · kept existing ${r.skipped.join(', ')}` : ''}` : `Nothing new${r.skipped.length ? ` · already here: ${r.skipped.join(', ')}` : ''}`,
        'ok',
      );
      setImporting(null);
    } catch (e) {
      err(e);
    }
  };
  const draftError = (() => {
    if (!draft) return null;
    for (const [k, v] of Object.entries(draft.settings)) {
      try {
        const o = JSON.parse(v || '{}');
        if (!o || typeof o !== 'object' || Array.isArray(o)) return `${LABEL[k] ?? k}: needs an object`;
      } catch {
        return `${LABEL[k] ?? k}: not valid JSON`;
      }
    }
    return null;
  })();
  const saveDraft = async () => {
    if (!draft || draftError) return;
    const body = {
      name: draft.name.trim(),
      description: draft.description,
      settings: Object.fromEntries(Object.entries(draft.settings).map(([k, v]) => [k, JSON.parse(v || '{}')])),
      wifi: draft.wifi.filter((w) => w.ssid.trim()).map((w) => ({ ssid: w.ssid.trim(), hidden: w.hidden, password: w.password || null })),
    };
    try {
      if (draft.id) await presets.update({ id: draft.id, ...body });
      else await presets.create(body);
      toast.push(`Saved ${body.name}`, 'ok');
      setDraft(null);
    } catch (e) {
      err(e);
    }
  };
  const doRemove = async () => {
    if (!removing) return;
    try {
      await presets.remove(removing.id);
      toast.push(`Deleted ${removing.name}`, 'ok');
      setRemoving(null);
    } catch (e) {
      err(e);
    }
  };

  const actions = canManage && (
    <div className="flex flex-wrap gap-2">
      <Button size="responsive" onClick={() => setCapture({ device_id: online[0]?.id ?? '', name: '', description: '', sections: ['display'] })}>
        Save from a unit
      </Button>
      <Button size="responsive" onClick={() => setImporting({ device_id: online[0]?.id ?? '', overwrite: false })}>
        Copy a unit's presets
      </Button>
      <Button size="responsive" variant="primary" onClick={() => setDraft(toDraft())}>
        New preset
      </Button>
    </div>
  );
  const rowActions = (p: Preset) => (
    <div className="flex items-center justify-end gap-2">
      {canApply && (
        <Button size="sm" variant="primary" onClick={() => setApplying(p)}>
          Apply…
        </Button>
      )}
      {canManage && (
        <Button size="sm" onClick={() => setDraft(toDraft(p))}>
          Edit
        </Button>
      )}
      {canManage && (
        <IconButton framed={false} danger label={`Delete ${p.name}`} className="text-xl text-fg5" onClick={() => setRemoving(p)}>
          ×
        </IconButton>
      )}
    </div>
  );
  return (
    <Section>
      <PageHeader desktopOnly title="Presets" meta={`${list.length} IN THE CLOUD · AVAILABLE TO EVERY UNIT`} actions={actions || undefined} />
      <div className="flex flex-col gap-3 md:hidden">
        {actions}
        <Card>
          <CardHeader title="Presets" meta={`${list.length}`} />
          {list.length === 0 && <EmptyState label="No presets yet" />}
          {list.map((p) => (
            <ListRow key={p.id} title={p.name} sub={`${sectionsTxt(p)}${p.wifi.length ? ` · Wi-Fi ${p.wifi.length}` : ''}`} trailing={rowActions(p)} />
          ))}
        </Card>
      </div>
      <Card className="hidden md:block">
        {list.length === 0 ? (
          <EmptyState label="No presets yet: save one from a unit, or copy a unit's own presets in" />
        ) : (
          <DataTable
            rows={list}
            rowKey={(p) => p.id}
            dense
            minWidth={760}
            columns={[
              {
                key: 'name',
                header: 'Preset',
                width: '1.4fr',
                cell: (p) => (
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate text-md font-medium">{p.name}</span>
                    {p.description && <span className="truncate font-mono text-xs text-fg4">{p.description}</span>}
                  </div>
                ),
              },
              {
                key: 'sets',
                header: 'Sets',
                width: '2fr',
                cell: (p) => (
                  <div className="flex flex-wrap gap-1">
                    {p.sections.map((s) => (
                      <Tag key={s}>{LABEL[s] ?? s}</Tag>
                    ))}
                    {p.wifi.map((w) => (
                      <Tag key={`w-${w.ssid}`}>Wi-Fi {w.ssid}</Tag>
                    ))}
                  </div>
                ),
              },
              { key: 'source', header: 'From', width: '1.2fr', cell: (p) => <CellMono className="truncate">{p.source}</CellMono> },
              { key: 'updated', header: 'Updated', width: '.8fr', cell: (p) => <CellMono>{p.updated_at ? fmtAgo(p.updated_at) : '—'}</CellMono> },
              { key: 'act', header: '', width: '220px', cell: rowActions },
            ]}
          />
        )}
      </Card>

      <ApplyPresetDialog open={!!applying} onClose={() => setApplying(null)} preset={applying} />

      <Dialog
        open={!!capture}
        onClose={() => setCapture(null)}
        title="Save from a unit"
        meta="THE UNIT MUST BE ONLINE · PASSWORDS NEVER TRAVEL"
        confirm={{ label: 'Save preset', onClick: doCapture, disabled: !capture?.device_id || !capture.name.trim() || !capture.sections.length || presets.busy, busy: presets.busy }}
      >
        {capture && (
          <div className="flex flex-col gap-3">
            <Field label="Unit">
              <Select block value={capture.device_id} onChange={(device_id) => setCapture({ ...capture, device_id })} options={unitOptions} aria-label="Unit" />
            </Field>
            <Field label="Name">
              <TextInput block value={capture.name} onChange={(e) => setCapture({ ...capture, name: e.target.value })} placeholder="Main hall" aria-label="Preset name" />
            </Field>
            <Field label="Notes">
              <TextInput block value={capture.description} onChange={(e) => setCapture({ ...capture, description: e.target.value })} aria-label="Preset notes" />
            </Field>
            <span className="micro text-fg3">What to save</span>
            {PRESET_SECTIONS.map((s) => (
              <label key={s.key} className="flex items-start gap-2 text-base">
                <Checkbox
                  checked={capture.sections.includes(s.key)}
                  onChange={() => setCapture({ ...capture, sections: capture.sections.includes(s.key) ? capture.sections.filter((x) => x !== s.key) : [...capture.sections, s.key] })}
                  label={`Save ${s.label}`}
                />
                <span className="flex flex-col">
                  <span>{s.label}</span>
                  <span className="font-mono text-xs text-fg4">{s.help}</span>
                </span>
              </label>
            ))}
          </div>
        )}
      </Dialog>

      <Dialog
        open={!!importing}
        onClose={() => setImporting(null)}
        title="Copy a unit's presets"
        meta="EVERY PRESET SAVED ON THAT UNIT BECOMES AVAILABLE TO ALL UNITS"
        confirm={{ label: 'Copy presets', onClick: doImport, disabled: !importing?.device_id || presets.busy, busy: presets.busy }}
      >
        {importing && (
          <div className="flex flex-col gap-3">
            <Field label="Unit">
              <Select block value={importing.device_id} onChange={(device_id) => setImporting({ ...importing, device_id })} options={unitOptions} aria-label="Unit to copy from" />
            </Field>
            <label className="flex items-center gap-2 text-base">
              <Switch checked={importing.overwrite} onChange={(overwrite) => setImporting({ ...importing, overwrite })} label="Replace presets with the same name" />
              <span>Replace cloud presets with the same name</span>
            </label>
          </div>
        )}
      </Dialog>

      <Dialog
        open={!!draft}
        onClose={() => setDraft(null)}
        size="lg"
        title={draft?.id ? `Edit ${draft.name}` : 'New preset'}
        meta="EACH SECTION IS CHECKED THE WAY A UNIT CHECKS IT WHEN YOU SAVE"
        confirm={{ label: 'Save preset', onClick: saveDraft, disabled: !draft?.name.trim() || !!draftError || presets.busy, busy: presets.busy }}
      >
        {draft && (
          <div className="flex flex-col gap-4">
            <Field label="Name">
              <TextInput block value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} aria-label="Preset name" />
            </Field>
            <Field label="Notes">
              <TextInput block value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} aria-label="Preset notes" />
            </Field>
            <span className="micro text-fg3">Sections · only the keys listed are changed on a unit; everything else stays as it is</span>
            {PRESET_SECTIONS.map((s) =>
              s.key in draft.settings ? (
                <div key={s.key} className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <span className="text-base font-medium">{s.label}</span>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        const { [s.key]: _drop, ...rest } = draft.settings;
                        setDraft({ ...draft, settings: rest });
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                  <TextArea
                    block
                    rows={Math.min(12, Math.max(3, draft.settings[s.key]!.split('\n').length))}
                    className="font-mono text-xs"
                    value={draft.settings[s.key]}
                    onChange={(e) => setDraft({ ...draft, settings: { ...draft.settings, [s.key]: e.target.value } })}
                    aria-label={`${s.label} settings`}
                  />
                </div>
              ) : null,
            )}
            <div className="flex flex-wrap gap-2">
              {PRESET_SECTIONS.filter((s) => !(s.key in draft.settings)).map((s) => (
                <Button key={s.key} size="xs" onClick={() => setDraft({ ...draft, settings: { ...draft.settings, [s.key]: '{\n}' } })}>
                  + {s.label}
                </Button>
              ))}
            </div>
            {draftError && <Note tone="warn">{draftError}</Note>}
            <span className="micro text-fg3">Wi-Fi networks · added on units that are online when you apply</span>
            {draft.wifi.map((w, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <TextInput value={w.ssid} placeholder="Network name" aria-label={`Wi-Fi ${i + 1} name`} onChange={(e) => setDraft({ ...draft, wifi: draft.wifi.map((x, j) => (j === i ? { ...x, ssid: e.target.value } : x)) })} />
                <TextInput
                  type="password"
                  autoComplete="new-password"
                  value={w.password}
                  placeholder={w.has_password ? 'Saved · type to replace' : 'Password'}
                  aria-label={`Wi-Fi ${i + 1} password`}
                  onChange={(e) => setDraft({ ...draft, wifi: draft.wifi.map((x, j) => (j === i ? { ...x, password: e.target.value } : x)) })}
                />
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox checked={w.hidden} onChange={(hidden) => setDraft({ ...draft, wifi: draft.wifi.map((x, j) => (j === i ? { ...x, hidden } : x)) })} label={`Wi-Fi ${i + 1} hidden`} />
                  Hidden
                </label>
                <IconButton framed={false} danger label={`Remove Wi-Fi ${i + 1}`} className="text-xl text-fg5" onClick={() => setDraft({ ...draft, wifi: draft.wifi.filter((_, j) => j !== i) })}>
                  ×
                </IconButton>
              </div>
            ))}
            {draft.wifi.length < 8 && (
              <Button size="xs" onClick={() => setDraft({ ...draft, wifi: [...draft.wifi, { ssid: '', password: '', hidden: false, has_password: false }] })}>
                + Wi-Fi network
              </Button>
            )}
            <Note>Wi-Fi passwords are stored encrypted and are never shown again.</Note>
          </div>
        )}
      </Dialog>

      <Dialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        title={removing ? `Delete ${removing.name}?` : ''}
        meta="UNITS KEEP THEIR SETTINGS AND THEIR OWN COPY"
        confirm={{ label: 'Delete', destructive: true, onClick: doRemove, busy: presets.busy }}
      >
        {removing && <Note>The preset leaves the cloud library. Nothing changes on any unit.</Note>}
      </Dialog>
    </Section>
  );
}

/** Captions page (cloud): the cloud library, one tap applies a preset to this unit. */
export function CloudPresetsCard({ deviceId, deviceName }: { deviceId: string; deviceName: string }) {
  const client = useClient();
  const me = useMe();
  const local = isLocalShell(client);
  const presets = usePresets(!local);
  const toast = useToast();
  const canApply = APPLY.has(me.data?.role ?? '');
  if (local || !canApply) return null;
  const list = presets.data ?? [];
  const apply = async (p: Preset) => {
    try {
      const r = await presets.apply({ id: p.id, device_ids: [deviceId], save_on_unit: true });
      const res = r.results[0];
      if (!res) toast.push(`${p.name} was not applied`, 'warn');
      else if (res.status === 'forbidden' || res.status === 'error') toast.push(`${p.name}: ${res.detail ?? res.status}`, 'warn');
      else toast.push(`${p.name} ${res.status === 'queued' ? `queued for ${deviceName}` : `applied to ${deviceName}`}`, 'ok');
    } catch (e) {
      toast.push((e as Error).message || 'Could not apply the preset', 'bad');
    }
  };
  return (
    <Card>
      <CardHeader dense title="Cloud presets" meta="ALL UNITS" />
      {list.length === 0 && <EmptyState label="None yet · Admin › Presets" />}
      {list.map((p) => (
        <div key={p.id} className="flex items-center justify-between gap-2 border-t border-line px-4 py-[9px]">
          <div className="flex min-w-0 flex-col">
            <span className="truncate text-md">{p.name}</span>
            <span className="truncate font-mono text-xs text-fg4">{sectionsTxt(p)}</span>
          </div>
          <Button size="xs" onClick={() => void apply(p)} disabled={presets.busy} aria-label={`Apply ${p.name} to ${deviceName}`}>
            Apply
          </Button>
        </div>
      ))}
    </Card>
  );
}
